import {Component, HostListener, OnDestroy, OnInit, ChangeDetectorRef} from '@angular/core';
import {FormBuilder, FormGroup, ReactiveFormsModule, Validators} from '@angular/forms';
import {IconComponent} from '../icon/icon.component';
import {Icons} from '../../../model/icon';
import {TextsComponent} from '../texts/texts.component';
import {NgClass, NgForOf, NgIf, NgStyle} from '@angular/common';
import {ThemeService} from '../../../services/theme.service';
import {Text} from '../../../model/text';
import {finalize} from 'rxjs';
import {ApiService} from '../../../services/api.service';
import {animate, style, transition, trigger} from '@angular/animations';
import {FeedbackService} from '../../../services/feedback-service';
import {MenuService} from '../../../services/menu-services';
import {text} from '../../../functions/localization';
import {scrollUp} from '../../../functions/scroll-up';

@Component({
  selector: 'app-feedback',
  imports: [
    ReactiveFormsModule,
    IconComponent,
    TextsComponent,
    NgIf,
    NgForOf,
    NgClass,
    NgStyle
  ],
  templateUrl: './feedback.component.html',
  styleUrl: './feedback.component.scss',
  animations: [
    trigger('slideInOut', [
      transition(':enter', [
        style({
          opacity: 0,
          transform: 'translateY(-10px) scale(0.95)',
          height: 0
        }),
        animate('200ms cubic-bezier(0.34, 1.56, 0.64, 1)', style({
          opacity: 1,
          transform: 'translateY(0) scale(1)',
          height: '*'
        }))
      ]),
      transition(':leave', [
        animate('150ms cubic-bezier(0.4, 0, 0.2, 1)', style({
          opacity: 0,
          transform: 'translateY(-5px) scale(0.95)',
          height: 0
        }))
      ])
    ]),
    trigger('fadeInOut', [
      transition(':enter', [
        style({ opacity: 0, transform: 'translateY(-15px) scale(0.98)' }),
        animate('300ms cubic-bezier(0.34, 1.56, 0.64, 1)', style({
          opacity: 1,
          transform: 'translateY(0) scale(1)'
        }))
      ]),
      transition(':leave', [
        animate('200ms cubic-bezier(0.4, 0, 0.2, 1)', style({
          opacity: 0,
          transform: 'translateY(-10px) scale(0.98)'
        }))
      ])
    ])
  ]
})
export class FeedbackComponent implements OnInit, OnDestroy {
  constructor(
    private fb: FormBuilder,
    public themeService: ThemeService,
    public apiService: ApiService,
    public feedValue: FeedbackService,
    public menuService: MenuService,
    private cdRef: ChangeDetectorRef
  ) {
    this.feedback = this.fb.group({
      fullName: ['', [Validators.required, Validators.minLength(2)]],
      company: [''],
      phoneNumber: ['', [Validators.required, Validators.pattern(/^\+?[0-9\s\-\(\)]+$/)]],
      email: ['', [Validators.required, Validators.email]],
      subject: ['', Validators.required],
      message: [''],
    });
  }

  formHeight: number = 0;
  readonly baseHeightOffset: number = 31;
  openList = false;
  isLoading = false;
  isSuccess = false;
  errorMessage = '';
  showConsentMessage = false;

  title: Text[] = [
    {text: ['Get ', 'Get '],},
    {text: ['in Touch', 'in Touch'], fontFamily: 'pd', fontStyle: 'italic'},
  ]

  textsEl: Text[] = [
    {text: ['Персональный travel-менеджер', 'Personal Travel Manager']},
    {text: ['Бронирование отелей', 'Hotel Booking']},
    {text: ['Аренда частных джетов', 'Private Jet Charter']},
    {text: ['VIP-сервисы в аэропортах', 'Airport VIP Services']},
    {text: ['Авиабилеты', 'Flight Tickets']},
    {text: ['Визовая поддержка', 'Visa Support']},
    {text: ['Круизы', 'Cruises']},
    {text: ['Аренда шале', 'Chalet Rental']},
    {text: ['Аренда яхт', 'Yacht Charter']},
    {text: ['Корпоративным клиентам', 'Corporate Services']},
    {text: ['Другое', 'Other']}
  ];

  value: string = '';
  currentSubjectText: Text = {text: ['', '']};
  read: boolean = false;
  isCheckboxWarning: boolean = false;
  isMobile: boolean = false;

  private warningTimeout: any = null;
  private resizeTimeout: any = null;
  private isCalculating: boolean = false;

  @HostListener('window:resize', ['$event'])
  onResize() {
    if (this.isMobile) {
      this.handleViewportChange();
    }
  }

  ngOnInit() {
    this.isMobile = window.innerWidth < 571;

    if (this.isMobile) {
      setTimeout(() => this.calculateFormHeight(), 100);
    }
    const feedbackTexts = this.feedValue.getFeedbackValue();
    if (feedbackTexts.length > 0) {
      const firstText = feedbackTexts[0];
      const rus = (firstText?.text && firstText.text[0]) ? firstText.text[0] : '';
      const eng = (firstText?.text && firstText.text[1]) ? firstText.text[1] : '';
      const texts = text([rus, eng]);
      this.value = texts;
      this.feedback.patchValue({
        subject: this.formatText(texts)
      });
    }
    if (window.innerWidth < 769) {
      this.title = [
        {text: ['Get in ', 'Get in '], transfer: true},
        {text: ['Touch', 'Touch'], fontFamily: 'pd', fontStyle: 'italic'},
      ]
    }
    this.feedback.valueChanges.subscribe(() => {
      this.showConsentMessage = false;
    });
    this.setCurrentSubjectText();
    this.detectBrowser();
  }

  ngOnDestroy() {
    if (this.resizeTimeout) {
      clearTimeout(this.resizeTimeout);
    }
    if (this.warningTimeout) {
      clearTimeout(this.warningTimeout);
    }
    this.feedValue.clearFeedbackValue();
  }

  private handleViewportChange() {
    if (this.resizeTimeout) {
      clearTimeout(this.resizeTimeout);
    }
    this.resizeTimeout = setTimeout(() => {
      this.calculateFormHeight();
      this.cdRef.detectChanges();
    }, 150);
  }

  private calculateFormHeight(): void {
    if (!this.isMobile || this.isCalculating) return;

    this.isCalculating = true;

    requestAnimationFrame(() => {
      const form = document.querySelector('form.feedback-container') as HTMLElement;
      if (form) {
        const height = form.getBoundingClientRect().height;
        this.formHeight = Math.ceil(height);
      }
      setTimeout(() => {
        this.isCalculating = false;
      }, 200);
    });
  }

  private detectBrowser(): void {
    const ua = navigator.userAgent.toLowerCase();
    if (ua.includes('yandex')) {
      document.documentElement.setAttribute('data-browser', 'yandex');
    }
  }

  onInputFocus() {
    scrollUp();
    this.menuService.setInputFocused(true);
    setTimeout(() => this.calculateFormHeight(), 50);
  }

  onInputBlur() {
    setTimeout(() => {
      const activeElement = document.activeElement as HTMLElement;
      if (activeElement?.tagName !== 'INPUT' && activeElement?.tagName !== 'TEXTAREA') {
        this.menuService.setInputFocused(false);
        setTimeout(() => this.calculateFormHeight(), 50);
      }
    }, 100);
  }

  onInput(textarea: HTMLTextAreaElement): void {
    textarea.style.height = 'auto';
    textarea.style.height = `${textarea.scrollHeight}px`;
  }

  onInputMob(textarea: HTMLTextAreaElement): void {
    textarea.style.height = 'auto';
    textarea.style.height = `${textarea.scrollHeight}px`;

    setTimeout(() => {
      this.calculateFormHeight();
    }, 50);
  }

  postFeedback() {
    if (!this.read) {
      this.showCheckboxWarning();
      return;
    }
    this.markAllAsTouched();
    if (this.feedback.invalid) {
      return;
    }
    this.isLoading = true;
    this.isSuccess = false;
    this.errorMessage = '';
    this.showConsentMessage = false;
    this.apiService.post('public/feedback', this.feedback.value)
      .pipe(finalize(() => this.isLoading = false))
      .subscribe({
        next: () => {
          this.isSuccess = true;
          this.feedback.reset();
          this.read = false;
          setTimeout(() => this.calculateFormHeight(), 100);
        },
        error: (error) => {
          this.errorMessage = error.error?.message || 'Error sending form. Please try again.';
          console.error('Feedback error:', error);
        }
      });
  }

  toggleRead() {
    this.read = !this.read;
    this.removeCheckboxWarning();
  }

  showCheckboxWarning() {
    this.isCheckboxWarning = true;
    if (this.warningTimeout) {
      clearTimeout(this.warningTimeout);
    }
    this.warningTimeout = setTimeout(() => {
      this.removeCheckboxWarning();
    }, 5000);
  }

  removeCheckboxWarning() {
    this.isCheckboxWarning = false;
    if (this.warningTimeout) {
      clearTimeout(this.warningTimeout);
      this.warningTimeout = null;
    }
  }

  markAllAsTouched() {
    Object.values(this.feedback.controls).forEach(control => {
      control.markAsTouched();
    });
  }

  isFieldInvalid(fieldName: string): boolean {
    const control = this.feedback.get(fieldName);
    if (fieldName === 'message') {
      return false;
    }
    return control ? control.invalid && (control.dirty || control.touched) : false;
  }

  getWrapperStyles(): { [key: string]: string } {
    return {}
    if (!this.isMobile) return {};
    const isFocused = this.menuService.getInputFocused();
    const viewportHeight = window.innerHeight;
    let wrapperHeight = isFocused
      ? this.formHeight
      : this.formHeight + viewportHeight;
    wrapperHeight = Math.max(wrapperHeight, 0);
    return {
      'height': wrapperHeight + 'px',
      'position': 'relative',
      'width': '100%'
    };
  }

  getStickyStyles(): { [key: string]: string } {
    return {}
    if (!this.isMobile) return {};
    const isFocused = this.menuService.getInputFocused();
    const viewportHeight = window.innerHeight;
    const stickyTop = isFocused
      ? 0
      : Math.max(0, viewportHeight - this.formHeight);
    return {
      'position': 'sticky',
      'top': stickyTop + 'px',
      'height': this.formHeight + 'px',
      'width': '100%',
      'z-index': '10',
      '--sticky-top': stickyTop + 'px'
    };
  }

  getMarginStyles(): { [key: string]: string } {
    if (!this.isMobile) return {};
    const isFocused = this.menuService.getInputFocused();
    const viewportHeight = window.innerHeight;
    const marginTop = isFocused ? '0' : `-${viewportHeight}px`;
    return {
      'margin-top': marginTop,
      'position': 'relative',
      'z-index': '20'
    };
  }

  selectItem(index: number): void {
    const selectedItem = this.textsEl[index];
    if (!selectedItem?.text?.[1]) return;
    this.currentSubjectText = selectedItem;
    this.value = selectedItem.text[1];
    this.openList = false;
    this.feedback.patchValue({
      subject: this.formatText(selectedItem.text[1])
    });
  }

  setCurrentSubjectText() {
    if (!this.value) {
      this.currentSubjectText = {text: ['', '']};
      return;
    }
    const foundItem = this.textsEl.find(item =>
      item?.text?.[1] && this.formatText(item.text[1]) === this.value
    );
    this.currentSubjectText = foundItem || {text: [this.value, this.value]};
  }

  formatText(input: string): string {
    return input.toUpperCase().replace(/\s+/g, '_');
  }

  openWhatsApp() {
    const phoneNumber = '+66991609711';
    const message = 'Hello! I would like to get more information about your services.';
    const url = `https://wa.me/${phoneNumber}?text=${encodeURIComponent(message)}`;
    window.open(url, '_blank');
  }

  openTelegram() {
    const username = 'ilovefirstclass';
    const message = 'Hello! I would like to get more information about your services.';
    const url = `https://t.me/${username}?text=${encodeURIComponent(message)}`;
    window.open(url, '_blank');
  }

  openEmail() {
    const email = 'info@ilovefirstclass.com';
    const subject = 'Inquiry about services';
    const body = 'Dear First Class Team,\n\nI would like to get more information about your services.\n\nBest regards,';
    const url = `mailto:${email}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    window.open(url, '_blank');
  }

  openPhoneCall() {
    const phoneNumber = '+66991609711';
    const url = `tel:${phoneNumber}`;
    window.open(url, '_blank');
  }

  openInstagramProfile() {
    const username = 'ilovefirstclass';
    const url = `https://www.instagram.com/${username}/`;
    window.open(url, '_blank');
  }

  getInvert() {
    return this.themeService.getTheme() === 'black-theme';
  }

  showSection(type: 'left' | 'right') {
    if (window.innerWidth > 769) return true;
    return type === 'left'
      ? this.menuService.getMobileForm()
      : !this.menuService.getMobileForm();
  }

  openPdf() {
    const x = 'app/assets/pdf/new/Privacy Policy.pdf';
    window.open(x, '_blank', 'noopener,noreferrer');
  }

  get policyText() {
    return text(['персональных данных', 'the privacy policy']);
  }

  feedback: FormGroup;
  protected readonly Icons = Icons;
  protected readonly text = text;
}
